/**
 * Appearance Service
 *
 * Fetches and saves per-workspace appearance settings from the elauth API.
 * Cascading fallback on the server: user+workspace → workspace config → reseller → wisteria wave defaults.
 */

// Auth API calls go through nginx /auth-api/ proxy (no direct cross-origin)
const AUTH_API_URL = '/auth-api'

const WORKSPACE_COOKIE_NAME = 'elauth_workspace_id'

export interface AppearanceSettings {
  theme: string
  color_scheme: string
  spacing_mode: string
  background_style: string
  custom_colors: Record<string, string>
}

function getWorkspaceId(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${WORKSPACE_COOKIE_NAME}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/**
 * Fetch appearance settings for the current user + workspace from elauth.
 * The server handles the cascading fallback chain.
 */
export async function fetchAppearance(): Promise<AppearanceSettings | null> {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {
    // silently ignore missing workspace cookie
    return null
  }

  try {
    const url = `${AUTH_API_URL}/me/appearance`
    const resp = await fetch(url, {
      credentials: 'include',
      headers: {
        'X-Workspace-Id': workspaceId,
      },
      signal: AbortSignal.timeout(8000),
    })

    if (!resp.ok) {
      // silently ignore non-OK response
      return null
    }

    const data = (await resp.json()) as { settings: AppearanceSettings }
    return data.settings ?? null
  } catch {
    // silently ignore fetch failure
    return null
  }
}

/**
 * Fetch appearance settings for multiple workspaces in a single request.
 * Returns a map of workspace ID → settings (with cascading fallback applied server-side).
 */
export async function fetchBulkAppearance(
  workspaceIds: string[],
): Promise<Record<string, AppearanceSettings>> {
  if (workspaceIds.length === 0) {return {}}

  try {
    const resp = await fetch(
      `${AUTH_API_URL}/me/appearance/bulk?workspace_ids=${workspaceIds.join(',')}`,
      {
        credentials: 'include',
        signal: AbortSignal.timeout(8000),
      },
    )

    if (!resp.ok) {return {}}

    const data = (await resp.json()) as { settings: Record<string, AppearanceSettings> }
    return data.settings ?? {}
  } catch {
    // silently ignore bulk fetch failure
    return {}
  }
}

/**
 * Save appearance settings for the current user + workspace to elauth.
 */
export async function saveAppearance(settings: Partial<AppearanceSettings>): Promise<AppearanceSettings | null> {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {return null}

  try {
    const resp = await fetch(`${AUTH_API_URL}/me/appearance`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspaceId,
      },
      body: JSON.stringify(settings),
      signal: AbortSignal.timeout(8000),
    })

    if (!resp.ok) {return null}

    const data = (await resp.json()) as { settings: AppearanceSettings }
    return data.settings ?? null
  } catch {
    // silently ignore save failure
    return null
  }
}
